import React, { useMemo, useState } from 'react';
import { Chip, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { PatternFormat } from 'react-number-format';
import Table from '../common/Table';

const STATUS_CHIP_CONFIG = {
    Active: { label: 'Active', color: 'success' },
    Suspended: { label: 'Suspended', color: 'error' },
    'Administrative Suspension': { label: 'Administrative Suspension', color: 'error' },
    invited: { label: 'Invite sent', color: 'info' },
    pending_approval: { label: 'Awaiting approval', color: 'warning' }
};

const AssociatesTable = ({
    members,
    invites,
    propertyId,
    canManage,
    globalFilter,
    onSuspend,
    onDelete,
    onInviteDelete
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    const data = useMemo(
        () => [
            ...(members || []).map(member => ({ ...member, source: 'member' })),
            ...(invites || []).map(invite => ({ ...invite, source: 'invite' }))
        ],
        [members, invites]
    );

    const handleMenuClick = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const handleSuspend = () => {
        onSuspend(selectedRow);
        handleMenuClose();
    };

    const handleDelete = () => {
        if (selectedRow.source === 'invite') onInviteDelete(selectedRow);
        else onDelete(selectedRow);
        handleMenuClose();
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: row => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
                Cell: ({ row }) =>
                    `${row.original.first_name || ''} ${row.original.last_name || ''}`
            },
            {
                Header: 'Email',
                accessor: row => row.email || '',
                Cell: ({ row }) => (
                    <Typography
                        sx={{ fontSize: '13px', textTransform: 'none' }}
                        component="span"
                        color={row.original.email ? 'text.primary' : 'text.secondary'}
                    >
                        {row.original.email || 'N/A'}
                    </Typography>
                )
            },
            {
                Header: 'Phone',
                accessor: row =>
                    row.phone?.number
                        ? `${row.phone.code || ''}${row.phone.number}`.trim()
                        : '',
                Cell: ({ row }) => {
                    const phone = row.original.phone;

                    if (!phone?.number) {
                        return (
                            <Typography
                                sx={{ fontSize: '13px' }}
                                component="span"
                                color="text.secondary"
                            >
                                N/A
                            </Typography>
                        );
                    }

                    return (
                        <Typography
                            sx={{ fontSize: '13px' }}
                            component="span"
                            color="text.primary"
                        >
                            <PatternFormat
                                value={phone.number}
                                displayType={'text'}
                                format={`+${phone.code || ''} (###)-###-####`}
                            />
                        </Typography>
                    );
                }
            },
            {
                Header: 'Status',
                accessor: 'status',
                className: 'cell-center',
                Cell: ({ row }) => {
                    const { status } = row.original;
                    const { label, color } = STATUS_CHIP_CONFIG[status] || {
                        label: status,
                        color: 'default'
                    };
                    return <Chip size="small" label={label} color={color} />;
                }
            },
            ...(canManage
                ? [
                      {
                          Header: 'Actions',
                          className: 'cell-right',
                          Cell: ({ row }) => (
                              <IconButton
                                  onClick={e => {
                                      e.stopPropagation();
                                      handleMenuClick(e, row.original);
                                  }}
                              >
                                  <MoreHoriz />
                              </IconButton>
                          )
                      }
                  ]
                : [])
        ],
        [canManage]
    );

    return (
        <>
            <Table columns={columns} data={data} externalGlobalFilter={globalFilter} />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {canManage && selectedRow?.source === 'member' && (
                    <MenuItem onClick={handleSuspend}>
                        {selectedRow.properties?.[propertyId]?.suspended
                            ? 'Unsuspend'
                            : 'Suspend'}
                    </MenuItem>
                )}
                {canManage && <MenuItem onClick={handleDelete}>Delete</MenuItem>}
            </Menu>
        </>
    );
};

export default AssociatesTable;
